import {
  add,
  isAfter,
  format,
  isSameDay,
  isSameMonth,
  isSameYear,
  eachDayOfInterval,
} from "date-fns";
import { utcToZonedTime, formatInTimeZone } from "date-fns-tz";
import { TaskStartDatePeriod } from "Models/task";
import { TimeSlotTask } from "Models/time.slot.task";
import { Timezone } from "Models/timezone";
import {
  getUserDateFormatPattern,
  getUserTimeFormatPattern,
  UserPreferences,
} from "Util/dateFormaterPreference";

export const MORNING_START = 6;
export const MORNING_END = 12;

export const AFTERNOON_START = 12;
export const AFTERNOON_END = 18;

export const EVENING_START = 18;
export const EVENING_END = 23;

const DAWN_START = 1;
const DAWN_END = 6;

export const TASK_DATE_MINUTES = 0;

interface GetStartDateRangeLabelOptions {
  considerEvening?: boolean;
  separator?: string;
  adjustStartTimeToDefault?: boolean;
  isFullDay?: boolean;
  preferences?: UserPreferences;
}

export const getStartDateRange = (
  startDate: string | Date,
  options: GetStartDateRangeLabelOptions = {}
) => {
  const { considerEvening = false, adjustStartTimeToDefault = false, isFullDay = false } = options;

  const date = new Date(startDate);

  if (adjustStartTimeToDefault) {
    date.setMinutes(0);
  }
  date.setSeconds(0);

  const stringHours = format(date, "HH");
  const hours = Number(stringHours);

  if (isFullDay) {
    const startTime = new Date(date);
    const endTime = new Date(date);

    startTime.setHours(MORNING_START);
    startTime.setMinutes(0);

    endTime.setHours(AFTERNOON_END);
    endTime.setMinutes(0);

    return { startTime, endTime };
  }

  if (hours < DAWN_END - 1) {
    const startTime = new Date(date);
    const endTime = new Date(date);

    if (adjustStartTimeToDefault) {
      startTime.setHours(DAWN_START);
      startTime.setMinutes(0);
    }

    endTime.setHours(DAWN_END);
    endTime.setMinutes(0);

    return { startTime, endTime };
  }

  if (hours < MORNING_END) {
    const startTime = new Date(date);
    const endTime = new Date(date);

    if (adjustStartTimeToDefault) {
      startTime.setHours(MORNING_START);
      startTime.setMinutes(0);
    }

    endTime.setHours(MORNING_END);
    endTime.setMinutes(0);

    return { startTime, endTime };
  }

  if (hours < AFTERNOON_END || !considerEvening) {
    const startTime = new Date(date);
    const endTime = new Date(date);

    if (adjustStartTimeToDefault) {
      startTime.setHours(AFTERNOON_START);
      startTime.setMinutes(0);
    }

    endTime.setHours(AFTERNOON_END);
    endTime.setMinutes(0);

    return { startTime, endTime };
  }

  if (hours <= EVENING_END) {
    const startTime = new Date(date);
    const endTime = new Date(date);

    if (adjustStartTimeToDefault) {
      startTime.setHours(EVENING_START);
      startTime.setMinutes(0);
    }

    endTime.setHours(EVENING_END);
    endTime.setMinutes(59);

    return { startTime, endTime };
  }

  return {};
};

const getStartDateRangeLabel = (
  startDate: string | Date,
  options: GetStartDateRangeLabelOptions = {}
) => {
  const { separator = "~", preferences } = options;
  const timePattern = getUserTimeFormatPattern(preferences);

  const { startTime, endTime } = getStartDateRange(startDate, options);

  if (!startTime || !endTime) {
    return "";
  }

  return `${format(startTime, timePattern)} ${separator} ${format(endTime, timePattern)}`;
};

const getStartDatePeriods = (date: string | Date) => {
  const morning = new Date(date);

  morning.setHours(MORNING_START);
  morning.setMinutes(0);
  morning.setSeconds(0);

  const afternoon = new Date(date);

  afternoon.setHours(AFTERNOON_START);
  afternoon.setMinutes(0);
  afternoon.setSeconds(0);

  const evening = new Date(date);

  evening.setHours(EVENING_START);
  evening.setMinutes(0);
  evening.setSeconds(0);

  const fullDay = new Date(date);

  fullDay.setHours(MORNING_START);
  fullDay.setMinutes(0);
  fullDay.setSeconds(0);

  return {
    morning,
    afternoon,
    evening,
    fullDay,
  };
};

const getEndDatePeriods = (date: string | Date) => {
  const morning = new Date(date);

  morning.setHours(MORNING_END);
  morning.setMinutes(0);
  morning.setSeconds(0);

  const afternoon = new Date(date);

  afternoon.setHours(AFTERNOON_END);
  afternoon.setMinutes(0);
  afternoon.setSeconds(0);

  const evening = new Date(date);

  evening.setHours(EVENING_END);
  evening.setMinutes(59);
  evening.setSeconds(0);

  return {
    morning,
    afternoon,
    evening,
  };
};

interface GetCompleteDateRangeParams {
  startDate?: string | Date | null | undefined;
  endDate?: string | Date | null | undefined;
  separator?: string;
  considerEvening?: boolean;
}

export const getCompleteDateRange = (params: GetCompleteDateRangeParams) => {
  const { endDate, startDate, separator, considerEvening } = params;
  const internalEndDate = endDate ? new Date(endDate) : undefined;
  const internalStartDate = startDate ? new Date(startDate) : undefined;

  if (!endDate && startDate) {
    return getStartDateRange(startDate, { separator, considerEvening });
  }

  return { startTime: internalStartDate, endTime: internalEndDate };
};

export const isSameDate = (_from: Date | string, _to: Date | string) => {
  const fromDate = new Date(_from);
  const toDate = new Date(_to);

  return (
    isSameDay(fromDate, toDate) && isSameMonth(fromDate, toDate) && isSameYear(fromDate, toDate)
  );
};

export const isTheSameDay = (date1: Date | string, date2: Date | string, timezone?: string) => {
  const day1 = formatInTimeZone(date1, timezone || "UTC", "yyyy-MM-dd");
  const day2 = formatInTimeZone(date2, timezone || "UTC", "yyyy-MM-dd");
  return day1 === day2;
};

const getDateRangeStep = (
  _from: Date | string,
  _to: Date | string,
  actualDate: Date | string,
  timezone?: string // Timezone opcional
): string => {
  // Função auxiliar para converter data para timezone específico
  const getDateInTimezone = (date: Date, tz: string) => {
    return utcToZonedTime(date, tz);
  };

  // Converte as datas de entrada
  const from = _from ? new Date(_from) : new Date();
  let to = _to ? new Date(_to) : new Date();
  const now = actualDate ? new Date(actualDate) : new Date();

  // Se timezone for especificado, converte as datas para o timezone local
  const fromDate = timezone ? getDateInTimezone(from, timezone) : from;
  let toDate = timezone ? getDateInTimezone(to, timezone) : to;
  const nowDate = timezone ? getDateInTimezone(now, timezone) : now;

  if (fromDate && toDate && isAfter(fromDate, toDate)) {
    // To avoid BUG of from is after to
    toDate = add(fromDate, { hours: 1 });
  }

  const interval = eachDayOfInterval({
    start: fromDate,
    end: toDate,
  });

  let actualStep = 1;
  const totalStep = interval.length || 1;

  if (interval.length) {
    interval.forEach((dateCompare, index) => {
      if (isSameDate(nowDate, dateCompare)) {
        actualStep += index;
      }
    });
  }

  return `(${actualStep}/${totalStep})`;
};

interface GetCompleteDateRangeLabelParams {
  startDate?: string | Date | null | undefined;
  endDate?: string | Date | null | undefined;
  separator?: string;
  currentDate?: string | Date | null | undefined;
  timeSlots?: Array<TimeSlotTask>;
  period?: TaskStartDatePeriod;
  timezone?: Timezone;
  formatFunction?: (date: Date, formatString: string) => string;
  preferences?: UserPreferences;
}

export const getCompleteDateRangeLabel = (params: GetCompleteDateRangeLabelParams): string => {
  const {
    endDate = null,
    startDate = null,
    separator,
    currentDate = new Date(),
    timeSlots = [],
    period,
    timezone,
    formatFunction,
    preferences,
  } = params;
  const internalStartDate = startDate ? new Date(startDate) : undefined;
  const internalEndDate = endDate ? new Date(endDate) : undefined;
  const currentTimezone = timezone?.name || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const datePattern = getUserDateFormatPattern(preferences);
  const timePattern = getUserTimeFormatPattern(preferences);

  const formatDateFunction = (date: Date, formatString: string, timezone?: string) => {
    if (formatFunction) return formatFunction(date, formatString);
    return formatInTimeZone(date, timezone!, formatString);
  };

  // if the start and end is on the same day = single day
  const getSingleDayRange = () => {
    if (!endDate && startDate) {
      const overrideLabel = getStartDateRangeLabel(startDate, { separator, preferences });

      if (overrideLabel) return overrideLabel;
    }

    let label = internalStartDate
      ? formatDateFunction(internalStartDate, timePattern, currentTimezone)
      : "";

    if (internalEndDate) {
      label += ` ${separator} ${formatDateFunction(internalEndDate, timePattern, currentTimezone)}`;
    }

    return label;
  };

  if (!startDate || !endDate) {
    return getSingleDayRange();
  }

  if (period === TaskStartDatePeriod.FIXED_TIME) {
    if (!internalStartDate || !internalEndDate) return "";

    const range = currentDate
      ? getDateRangeStep(startDate, endDate, currentDate, timezone?.name)
      : "";

    const startLabel = formatDateFunction(new Date(currentDate!), datePattern, currentTimezone);

    const timeRangeLabel = `(${formatDateFunction(
      internalStartDate,
      timePattern,
      currentTimezone
    )} - ${formatDateFunction(internalEndDate, timePattern, currentTimezone)})`;

    return `${startLabel} ${range} ${timeRangeLabel}`;
  }

  if (period === TaskStartDatePeriod.VARIABLE_TIME && timeSlots.length) {
    if (!currentDate || !internalStartDate || !internalStartDate) return "";

    const timezonedCurrentDate = utcToZonedTime(currentDate, currentTimezone);

    const currentDay = timeSlots.find((value) => {
      const timezonedDate = utcToZonedTime(value.date, currentTimezone);
      return timezonedDate.getDay() === timezonedCurrentDate.getDay();
    });

    if (currentDay) {
      const range = currentDate
        ? getDateRangeStep(startDate, endDate, currentDate, timezone?.name)
        : "";
      const startLabel = formatDateFunction(
        new Date((currentDay?.date as string).split(" ")[0]),
        datePattern,
        currentTimezone
      );

      const dateFormatted = formatDateFunction(
        new Date((currentDay?.date as string).split(" ")[0]),
        "yyyy-MM-dd",
        currentTimezone
      );

      const timeFromFormatted = format(
        new Date(`${dateFormatted}T${currentDay.from}:00`),
        timePattern
      );

      const timeToFormatted = format(new Date(`${dateFormatted}T${currentDay.to}:00`), timePattern);

      const timeRangeLabel = `(${timeFromFormatted} - ${timeToFormatted})`;

      return `${startLabel} ${range} ${timeRangeLabel}`;
    }
  }

  const startAndEndIsSame = isSameDate(startDate, endDate);

  if (startAndEndIsSame) return getSingleDayRange();

  // if the start and end is in the different days = multi day
  const geMultiDayRange = () => {
    if (!internalStartDate || !internalEndDate) return "";

    const range = currentDate
      ? getDateRangeStep(startDate, endDate, currentDate, timezone?.name)
      : "";

    const currentDateLabel = currentDate
      ? formatDateFunction(new Date(currentDate), datePattern, currentTimezone)
      : formatDateFunction(internalStartDate, datePattern, currentTimezone);

    const startTimeLabel = formatDateFunction(internalStartDate, timePattern, currentTimezone);
    const endTimeLabel = formatDateFunction(internalEndDate, timePattern, currentTimezone);

    return `${currentDateLabel} ${range} (${startTimeLabel} - ${endTimeLabel})`.trim();
  };

  if ((!endDate && startDate) || period !== TaskStartDatePeriod.MULTI_DAY)
    return getSingleDayRange();

  return geMultiDayRange();
};

export { getStartDateRangeLabel, getStartDatePeriods, getEndDatePeriods };
